import { Search, SlidersHorizontal } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookCover, BookProgress, EmptyState, PageTitle, ProgressBar } from '../../components/ui';
import type { BookStatus } from '../../domain/entities/types';
import { useBooks } from '../../hooks/useData';
import { getProgressLabel, normalizeText, statusLabel } from '../../utils/core';
import { compareBooks, type LibraryOrder } from './sortBooks';
import './LibraryPage.css';

const filters: Array<{ value: 'all' | BookStatus; label: string }> = [
  { value: 'all', label: 'Todos' }, { value: 'reading', label: 'Lendo' },
  { value: 'want_to_read', label: 'Quero ler' }, { value: 'finished', label: 'Lidos' },
  { value: 'paused', label: 'Pausados' }, { value: 'abandoned', label: 'Abandonados' }
];

const RatingStars = ({ rating }: { rating: number }) => {
  const normalizedRating = Math.min(5, Math.max(0, rating));
  const label = normalizedRating.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

  return (
    <span className="rating-stars" role="img" aria-label={`Avaliação: ${label} de 5 estrelas`} title={`${label} de 5 estrelas`}>
      <span className="rating-stars__base" aria-hidden="true">★★★★★</span>
      <span className="rating-stars__fill" aria-hidden="true" style={{ width: `${(normalizedRating / 5) * 100}%` }}>★★★★★</span>
    </span>
  );
};

export const LibraryPage = () => {
  const books = useBooks();
  const [filter, setFilter] = useState<'all' | BookStatus>('all');
  const [query, setQuery] = useState('');
  const [order, setOrder] = useState<LibraryOrder>('recent');
  const visible = useMemo(() => {
    const normalized = normalizeText(query);
    const result = books.filter((book) => (filter === 'all' || book.status === filter) && (!normalized || book.normalizedTitle.includes(normalized) || book.normalizedAuthor.includes(normalized)));
    return result.sort(compareBooks(order));
  }, [books, filter, query, order]);

  return (
    <div className="page library-page">
      <PageTitle eyebrow="Sua coleção" title="Biblioteca" action={<span className="book-count">{books.length}</span>} />
      <div className="library-controls">
        <div className="search-row">
          <label className="search-box"><Search /><span className="sr-only">Pesquisar livros</span><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Título ou autor" /></label>
          <label className="sort-button" title="Ordenar"><SlidersHorizontal /><select aria-label="Ordenar biblioteca" value={order} onChange={(e) => setOrder(e.target.value as LibraryOrder)}><option value="recent">Recentes</option><option value="title">Título</option><option value="author">Autor</option><option value="created">Data adicionada</option><option value="finished">Data concluída</option><option value="rating">Avaliação</option><option value="progress">Progresso</option></select></label>
        </div>
        <div className="chip-row" role="tablist" aria-label="Filtrar biblioteca">
          {filters.map(({ value, label }) => <button key={value} role="tab" aria-selected={filter === value} className={filter === value ? 'is-active' : ''} onClick={() => setFilter(value)}>{label}</button>)}
        </div>
      </div>
      {visible.length === 0 ? <EmptyState title="Nenhum livro por aqui">Tente outro filtro ou adicione um novo livro à sua estante.</EmptyState> : (
        <div className="book-list">
          {visible.map((book) => {
            const isFinished = book.status === 'finished';
            const showProgress = book.status === 'reading' || isFinished;

            return (
              <Link key={book.id} to={`/books/${book.id}`} className={`book-list-item${isFinished ? ' book-list-item--finished' : ''}`}>
                <BookCover book={book} size="medium" />
                <div className="book-list-item__body">
                  <span className={`status-badge status-badge--${book.status}`}>{statusLabel[book.status]}</span>
                  <h2>{book.title}</h2>
                  <p>{book.author}</p>
                  {showProgress && (
                    <div className="book-list-item__progress">
                      <small>{isFinished ? '100% concluído' : getProgressLabel(book.progress)}</small>
                      {isFinished ? <ProgressBar value={100} /> : <BookProgress book={book} compact />}
                    </div>
                  )}
                </div>
                {book.rating ? <RatingStars rating={book.rating} /> : null}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
